import { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import { apiClient } from '../utils/api-client';
import type { ArticleHead } from '../features/articles/types';

const MAX_POLL_DURATION_MS = 10 * 60 * 1000;

interface BackgroundLoaderContextValue {
  articles: ArticleHead[];
  articlesLoading: boolean;
  articlesError: string | null;
  refreshArticles: () => Promise<void>;
}

const BackgroundLoaderContext = createContext<BackgroundLoaderContextValue | undefined>(undefined);

export function useBackgroundLoader(): BackgroundLoaderContextValue {
  const ctx = useContext(BackgroundLoaderContext);
  if (!ctx) throw new Error('useBackgroundLoader must be used within BackgroundLoaderProvider');
  return ctx;
}

function sortNewestFirst(articles: ArticleHead[]): ArticleHead[] {
  return [...articles].sort((a, b) => {
    const dateA = a.createdAt || a.timestamp || '';
    const dateB = b.createdAt || b.timestamp || '';
    return dateB.localeCompare(dateA);
  });
}

export const BackgroundLoaderProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [articles, setArticles] = useState<ArticleHead[]>([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [articlesError, setArticlesError] = useState<string | null>(null);

  const articlesRef = useRef<ArticleHead[]>([]);
  useEffect(() => { articlesRef.current = articles; }, [articles]);

  const fetchArticles = useCallback(async () => {
    try {
      const response = await apiClient.get<{ articles: ArticleHead[] }>('/reader/articles');
      setArticles(sortNewestFirst(response.articles));
      setArticlesError(null);
    } catch {
      setArticlesError('Failed to load articles');
    } finally {
      setArticlesLoading(false);
    }
  }, []);

  const refreshArticles = useCallback(async () => {
    await fetchArticles();
  }, [fetchArticles]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  // Poll while any article is still processing; stop when all reach a
  // terminal state (ready or error).
  useEffect(() => {
    const hasIncomplete = articles.some(
      a => a.processingStatus !== 'ready' && a.processingStatus !== 'error'
    );
    if (!hasIncomplete) return;

    const startTime = Date.now();
    const intervalId = setInterval(() => {
      if (Date.now() - startTime > MAX_POLL_DURATION_MS) {
        setArticles(prev => prev.map(a =>
          a.processingStatus !== 'ready' && a.processingStatus !== 'error'
            ? { ...a, processingStatus: 'error' }
            : a
        ));
        clearInterval(intervalId);
        return;
      }
      fetchArticles();
    }, 1000);

    return () => clearInterval(intervalId);
  }, [articles.some(a => a.processingStatus !== 'ready' && a.processingStatus !== 'error'), fetchArticles]);

  const value = useMemo(
    () => ({ articles, articlesLoading, articlesError, refreshArticles }),
    [articles, articlesLoading, articlesError, refreshArticles],
  );

  return (
    <BackgroundLoaderContext.Provider value={value}>
      {children}
    </BackgroundLoaderContext.Provider>
  );
};
